// „Profilom" — a bejelentkezett user SAJÁT self-service profil-oldala. EGY nagy, rögzített üveg-felület
// (card-glass-lg), amelyen BELÜL görget — pont mint a lista-oldalak fix kártyája (konzisztens felület). A szekciók
// (fejléc · Alapadatok · Kapcsolat · Integrációk · Értesítések · Jelszó · 2FA) csak cím + térköz, NINCS külön
// blokk-keret és NINCS elválasztó vonal. A jog-kapu itt NEM jog-alapú (saját profil).
import { createFileRoute } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';

import { AppShell } from '@/components/shared/app-shell';
import { EmptyState } from '@/components/shared/empty-state';
import { FormPage } from '@/components/shared/form-page';
import { Skeleton } from '@/components/ui/skeleton';
import { ImpersonationFence } from '@/features/impersonation/components/impersonation-fence';
import { profileKeys } from '@/features/profile/api';
import { AvatarUploader } from '@/features/profile/components/avatar-uploader';
import { PasswordCard } from '@/features/profile/components/password-card';
import { ProfileDetailsForm } from '@/features/profile/components/profile-details-form';
import { TwoFactorCard } from '@/features/profile/components/two-factor-card';
import { api } from '@/lib/api';
import { requireAuth } from '@/lib/auth';
import { formatName } from '@/lib/format-name';

export const Route = createFileRoute('/profile')({
  beforeLoad: () => requireAuth(),
  component: ProfilePage,
});

/** Form-oldal shell: NINCS kártya-felület (háttér/keret) — a tartalom közvetlenül a téma-tudatos oldal-háttéren ül
 *  (settings best practice: GitHub/Linear/Vercel). A blokk marad (teljes magasság) és CSAK belül görget; oldalt
 *  nincs kártya-margó, csak minimális belső padding. Balra igazított, olvashatósági max-szélesség. */
function ProfileShell({ children }: { children: JSX.Element }): JSX.Element {
  // A konténer a KÖZÖS `FormPage` (ez a minta innen, a profil-oldalról származik, és időközben a form-oldalak
  // is ezt használják → egy helyen él a görgetés és az igazítás; CLAUDE.md 4. főszabály).
  return (
    <AppShell>
      <FormPage>{children}</FormPage>
    </AppShell>
  );
}

function ProfilePage(): JSX.Element {
  const { t, i18n } = useTranslation();
  const query = api.profile.useQuery(profileKeys.detail(), {});
  // „Egyéb adatok" (születésnap, névnap, gyerekek) — ez a route a BELSŐ Profilom (a partner a `portal.profile`-t
  // kapja, ott ez a lekérés nincs). 404 = nincs munkatársi adatlap (pl. gépi fiók) → a rész nem jelenik meg.
  const personalQuery = api.profilePersonal.useQuery(
    profileKeys.personal(),
    {},
    { queryKey: profileKeys.personal(), retry: false },
  );
  const personal = personalQuery.data?.status === 200 ? personalQuery.data.body : null;
  const profile = query.data?.status === 200 ? query.data.body : undefined;
  // Az avatar-KÉP a SAJÁT profil-GET-jéből (self-contained oldal); a részleg-SZÍN a /session-ből (a
  // navbarral azonos forrás — a profil-GET nem hordoz department-színt). A mentés a ['session']-t is
  // invalidálja, így a navbar avatar szinkronban marad.
  const session = api.session.useQuery(['session'], {});
  const teamColor = session.data?.status === 200 ? session.data.body.teamColor : null;

  if (query.isLoading) {
    return (
      <ProfileShell>
        <div className="space-y-5">
          <Skeleton className="h-16 w-full" />
          <Skeleton className="h-48 w-full" />
          <Skeleton className="h-48 w-full" />
        </div>
      </ProfileShell>
    );
  }

  if (!profile) {
    return (
      <ProfileShell>
        <EmptyState title={t('profile.loadErrorTitle')} description={t('profile.loadErrorDesc')} />
      </ProfileShell>
    );
  }

  const name = formatName(
    {
      givenName: profile.givenName,
      familyName: profile.familyName,
      displayName: profile.displayName ?? profile.nickname ?? profile.email,
    },
    i18n.language,
  );

  return (
    <ProfileShell>
      <div className="space-y-8">
        {/* Fejléc: NAGY, középre igazított, INTERAKTÍV avatar (feltöltés/módosítás/törlés). A név/e-mail
            SZÁNDÉKOSAN nincs itt — úgyis szerkeszthetően ott van lent (Alapadatok/Kapcsolat). */}
        {/* MEGSZEMÉLYESÍTÉS-FAL az AVATARON is: az Odin a `/iam/me/avatar/*`-ot `@DenyImpersonation()`-nel
            zárja, tehát más nevében a feltöltés/törlés 403 lenne. (A PARTNER-profil lapon nincs
            avatar-feltöltő, ott a kép a keret-adatból jön.) */}
        <ImpersonationFence hint={false}>
          <AvatarUploader name={name} avatarUrl={profile.avatarUrl} teamColor={teamColor} />
        </ImpersonationFence>

        <ProfileDetailsForm profile={profile} personal={personal} />
        <PasswordCard />
        <TwoFactorCard
          mfaEnabled={profile.mfaEnabled}
          twoFactorPending={profile.twoFactorPending}
        />
      </div>
    </ProfileShell>
  );
}
